import React, {useState, useEffect} from "react";
import HamburgerMenu from "../../../src/components/HamburgerMenu";
import{ useSelector, useDispatch} from "react-redux";
import {useFetchAssignedProjectByUserQuery} from "../../features/projectApiSlice";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import Helmet from 'react-helmet';
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {motion} from "framer-motion";
import {logout} from "../../../src/features/authSlice";
import {useLogoutMutation} from "../../../src/features/userApiSlice";
import {useSignInEmployeeRequestMutation,
    useSignOutEmployeeRequestMutation,
    useGetCurrentWorkSessionQuery } from "../../../src/features/attendanceApiSlice";
import {SessionTimeout} from "../../../src/screens/AttendenceScreen/SessionTimeOut";

import {
    MapPin,
    Building2,
    User,
    Clock,
    LogIn,
    LogOut,
    CheckCircle2,
} from "lucide-react";


export const EmployeeClockScreen = () => {
const {userInfo} = useSelector((state: any) => state).auth;
const userId = userInfo?.id;
const {data: projects, isLoading,} = useFetchAssignedProjectByUserQuery(userId)

const [signInEmployeeRequest] = useSignInEmployeeRequestMutation();
const [signOutEmployeeRequest] = useSignOutEmployeeRequestMutation();
const {data: currentSession, isLoading: isCurrentSessionLoading, refetch: refetchCurrentSession } = useGetCurrentWorkSessionQuery(userId);

const dispatch = useDispatch();

const [showSessionWarning, setShowSessionWarning] = useState(false);
const [remainingSeconds, setRemainingSeconds] = useState(20);
const [logoutApiCall] = useLogoutMutation();
const navigate = useNavigate();

    const SELECTED_PROJECT_KEY = "ksolution_selected_project"

    const [selectedProjectId, setSelectedProjectId] = useState<string| null>(() => {
        return localStorage.getItem(SELECTED_PROJECT_KEY);
    });

const isEmployeeSignedIn = currentSession?.isSignedIn === true;

const activeProjectId = currentSession?.userSession?.projectId ?? null;

const signInTime = currentSession?.userSession?.signInTime ?? null;

const handleSelectProject = (projectId: string) => {
    if (isEmployeeSignedIn) return;

    setSelectedProjectId(projectId);
    localStorage.setItem(SELECTED_PROJECT_KEY, projectId);
};

    // Sign In Employee
    const onSignInEmployeeHandler = async () => {

        if (!userInfo) {
            toast.error("You must be logged in to create a project");
            return;
        }

        if (!selectedProjectId) {
            toast.error("Please select a project first");
            return;
        }

        if (isEmployeeSignedIn) {
            toast.error("You are already signed in");
            return;
        }

        var siteCode = "SITE-001-Clarksville"
        var deviceId = "DEVICE-002"


        try {
            const newAttendance = {
                projectId: selectedProjectId,
                siteCode: siteCode,
                deviceId: deviceId,

            };
            await signInEmployeeRequest(newAttendance).unwrap();

            // added from fetchCurrentSession
            await refetchCurrentSession();

            toast.success("Sign In Successfully");

        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to create a project");
        }
    };

    const logoutHandler = async () => {
        try {
            // @ts-ignore
            await logoutApiCall().unwrap()
            // @ts-ignore
            dispatch(logout())

            navigate("/");

        } catch (error) {
            console.log(error);
        }
    }

    // Sign Out Employee
    const onSignOutEmployeeHandler = async () => {

        if (!userInfo) {
            toast.error("You must be logged in to create a project");
            return;
        }

        const siteCode = "SITE-001-Clarksville"
        const deviceId = "DEVICE-002"

        try {
            const attendanceRequest = {
                projectId: selectedProjectId,
                siteCode: siteCode,
                deviceId: deviceId,

            };

            await signOutEmployeeRequest(attendanceRequest).unwrap();

            localStorage.removeItem(SELECTED_PROJECT_KEY);
            setSelectedProjectId(null);

            toast.success("Sign Out Successfully");

            // Wait for 3 Seconds
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Enable Logout
            logoutHandler();


        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to create a project");
        }
    };

    useEffect(() => {
        if (!projects) return;

        if (!selectedProjectId) return;

        const projectStillAssigned = projects.some(
            (project: any) => project.projectId === selectedProjectId && project.isActive
        );

        if (!projectStillAssigned) {
            setSelectedProjectId(null);
            localStorage.removeItem(SELECTED_PROJECT_KEY);
        }
    }, [projects, selectedProjectId]);

    useEffect(() => {
        if (isEmployeeSignedIn) {
            setRemainingSeconds(20);
            setShowSessionWarning(true);
        } else {
            setShowSessionWarning(false);
        }
    }, [isEmployeeSignedIn]);

    return (
        <main className={"flex flex-col"}>
            <Helmet>
                <title>Employee Clock In</title>
                <meta name="description" content="Employee Clock In" />
            </Helmet>


            {isLoading ? (
                <CustomLoaderSmall />
            ) :  isCurrentSessionLoading ? (
                <CustomLoaderSmall />
            ) :
                (
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.4}}
                >
                    {/* Header Navigation */}
                    <HamburgerMenu projectId={selectedProjectId} />

                    {/* Main Screen */}
                    <div className="min-h-2 bg-slate-200 flex items-center justify-center p-4">

                        <div className="w-full max-w-md">

                            {/* Header */}
                            <div className="text-center mb-2">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 text-white mb-4 shadow-lg">
                                    <User size={30} />
                                </div>

                                <h1 className="text-2xl font-bold text-slate-900">
                                    Welcome, {userInfo?.userName}
                                </h1>

                                <p className="text-sm text-slate-500 mt-1">
                                    Employee Project ID: {userInfo?.employeeNumber}
                                </p>
                            </div>

                            {/* Main Card */}
                            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                                {/* Project Information */}
                                <div className="p-2">

                                    <div className="flex items-center gap-2 mb-4">
                                        <Building2
                                            size={20}
                                            className="text-blue-600"
                                        />

                                        <h2 className="font-semibold text-slate-900">
                                            Recent Projects
                                        </h2>
                                    </div>

                                    <div className="bg-slate-50 rounded-xl p-1 border border-slate-200"
                                            >

                                        {projects?.map((project: any, index: number)  => (
                                            <div key={`${project.id}`}
                                                 onClick={() => {
                                                     handleSelectProject(project.projectId);
                                                 }}
                                                 className={"rounded-2xl p-1 mb-2 cursor-pointer border-2 transition-all" +
                                                     `${selectedProjectId === project.projectId ?
                                                 "border-blue-500 bg-blue-100" : "border-transparent bg-white hover:bg-slate-400"}
                                                 ${isEmployeeSignedIn ? "cursor-not-allowed opacity-80" : ""}
                                                 `}
                                            >
                                                
                                                <h3 className="font-semibold text-lg text-slate-900">
                                                    {project?.projectName}
                                                </h3>

                                                <p className="text-sm text-slate-500 mt-1">
                                                    Project #{project?.projectNumber}
                                                </p>

                                                <div className="flex items-start mt-4 p-1">
                                                    <MapPin
                                                        size={18}
                                                        className="text-slate-400 mt-0.5 flex-shrink-0"
                                                    />

                                                    <span className="text-sm text-slate-600">
                                                        {project.location}
                                                    </span>
                                                </div>

                                            </div>
                                        ))}

                                    </div>

                                    {/* Current Status */}
                                    <div className="mt-2">

                                        {isEmployeeSignedIn  ? (
                                            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-1">

                                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                                                    <CheckCircle2
                                                        size={22}
                                                        className="text-green-600"
                                                    />
                                                </div>

                                                <div>
                                                    <p className="font-semibold text-green-800">
                                                        Currently Signed In
                                                    </p>

                                                    {signInTime && (
                                                        <p className="text-sm text-green-600">
                                                            Since {signInTime}
                                                        </p>
                                                    )}
                                                </div>

                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-2">

                                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200">
                                                    <Clock
                                                        size={21}
                                                        className="text-slate-600"
                                                    />
                                                </div>

                                                <div>
                                                    <p className="font-semibold text-slate-800">
                                                        Not Currently Signed In
                                                    </p>

                                                    <p className="text-sm text-slate-500">
                                                        Select Sign In to start your work session.
                                                    </p>
                                                </div>

                                            </div>
                                        )}

                                    </div>

                                    {/* Clock Buttons */}
                                <div className={"grid grid-col-2 gap-4 mt-2"}>

                                    {/* Sign In */}
                                    <button
                                        type="button"
                                        disabled={isEmployeeSignedIn ||
                                        !selectedProjectId}
                                        onClick={
                                            onSignInEmployeeHandler
                                        }


                                        className={`
                                                    flex flex-col items-center justify-center
                                                    gap-2 min-h-[100px]
                                                    rounded-xl
                                                    font-semibold
                                                    transition
        ${
                                            isEmployeeSignedIn || !selectedProjectId
                                                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                                : "bg-green-500 text-white hover:bg-green-800 active:scale-[0.98]"
                                        }
    `}
                                    >
                                        <LogIn size={32} />

                                        <span className="text-lg"> Sign In </span>

                                        <span className={"text-sm font-normal opacity-70"}>
                                            {isEmployeeSignedIn ? "Already Signed In" : selectedProjectId ? "Start Work": "Select Project"}
                                        </span>
                                    </button>

                                    {/* Sign Out */}
                                    <button
                                        type="button"
                                        disabled={!isEmployeeSignedIn}
                                        onClick={() => {
                                           onSignOutEmployeeHandler()
                                        }}
                                        className={`flex flex-col items-center justify-center gap-2 min-h-[100px]
                                                   rounded-xl font-semibold transition

                                            ${
                                            !isEmployeeSignedIn
                                                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                                : "bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]"
                                        }
        `}
                                    >
                                        <LogOut size={32} />

                                        <span className="text-lg"> Sign Out </span>

                                        {isEmployeeSignedIn && (
                                            <span className="text-xs font-normal opacity-80">
                                                End Work
                                            </span>
                                        )}
                                    </button>

                                </div>


                                </div>

                                {/* Footer */}
                                <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 text-center">

                                    <p className="text-xs text-slate-500">
                                        Employee Time Clock
                                    </p>

                                    <p className="text-xs text-slate-400 mt-1">
                                        Please verify the project and location before signing in.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {isEmployeeSignedIn && (
                            <SessionTimeout isOpen={showSessionWarning} remainingSeconds={remainingSeconds} onStaySignedIn={
                                () => {
                                    setShowSessionWarning(false);
                                }}
                                            onLogout={() => {
                                                setShowSessionWarning(false)
                                                logoutHandler();
                                            }}
                            />

                        )}


                    </div>


                </motion.div>
            )}
            </main>
    );
};

export default EmployeeClockScreen;

